import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Region from "../api/Region";
import { toast } from "react-hot-toast";
import { emitCustomEvent } from "react-custom-events";
import Company from "../api/Company";
import Input from "../components/form/Input";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/organisms/Layout";
import FormSelect from "../components/atoms/FormSelect";
import { Loader2 } from "lucide-react";

const AddRegion = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("-1");
  const [loading, setLoading] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState(null);

  useEffect(() => {
    document.title = "HeyHire Portal-Add New Region";
    loadCompanies();
  }, []);

  const companyOptions = useMemo(() => {
    return companies?.map((info) => ({
      value: info?.id,
      label: info?.name,
    }));
  }, [companies]);

  const addEditRegion = () => {
    setLoading(true);
    let body = {
      company_id: selectedCompany,
      name: name,
    };
    Region.create(body)
      .then(() => {
        toast.success(t("regions.region-added"));
        emitCustomEvent("regionChanged");
        navigate("/companies/regions");
      })
      .catch((error) => {
        if (error.response && error.response.status === 422) {
          setValidationErrors(error.response.data.errors);
        } else {
          if (error?.response?.status !== 403) {
            toast.error(t("common.something-went-wrong-please-try-again"));
          }
        }
      })
      .then(() => setLoading(false));
  };

  const loadCompanies = useCallback(() => {
    setCompaniesLoading(true);
    Company.getAll()
      .then((response) => {
        setCompanies(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedCompany(response.data.data[0].id);
        }
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          toast.error(t("common.something-went-wrong-please-try-again"));
        }
      })
      .finally(() => {
        setCompaniesLoading(false);
      });
  }, [t]);

  return (
    <DashboardLayout darmo={true}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">
            {t("regions.add-a-region")}
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Fill in the information below to create a new region.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white shadow-sm ring-1 ring-gray-300 rounded-xl p-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addEditRegion();
            }}
            className="space-y-8"
          >
            {/* Region Information Section */}
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">
                Region Information
              </h3>
              <p className="mt-1 text-sm leading-6 text-gray-600">
                Basic details about the region.
              </p>

              <div className="mt-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2">
                <div className="col-span-full sm:col-span-1">
                  <Input
                    darmo={true}
                    name="name"
                    label={t("common.name")}
                    type="text"
                    value={name}
                    placeholder={t("regions.add-a-region-name")}
                    error={
                      validationErrors && validationErrors.name
                        ? validationErrors.name[0]
                        : null
                    }
                    onChange={(e) => setName(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div className="col-span-full sm:col-span-1">
                  <FormSelect
                    label="Select Company"
                    placeholder={
                      companiesLoading ? "Loading companies..." : "Select Company"
                    }
                    value={selectedCompany?.label}
                    options={companyOptions}
                    isLoading={companiesLoading}
                    error={
                      validationErrors && validationErrors.company_id
                        ? validationErrors.company_id[0]
                        : null
                    }
                    onChange={(e) => setSelectedCompany(e?.value)}
                    darmo={true}
                  />
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="border-t border-gray-900/10 pt-8 flex justify-end gap-x-3">
              <button
                type="button"
                onClick={() => navigate("/companies/regions")}
                className="rounded-lg px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || companiesLoading}
                className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("common.add")
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddRegion;
